import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AssetFilters } from '../../../../model/filters/asset-filters';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { LoggedUser } from '../../../../common/logged-user.service';
import { translate } from '@ngneat/transloco';
import { EntityWithUuid } from '../../../../model/custom/entity-with-uuid';
import { Asset } from '../../../../model/asset';
import { Organization } from '../../../../model/organization';
import { Tenant } from '../../../../model/tenant';
import { TenantsService } from '../../../../services/tenants.service';
import { AssetsService } from '../../../../services/assets.service';
import { DeviceCategory } from '../../../../model/device-category';
import { AssetRepresentation } from '../../../../model/asset-representation';
import { SortOrder } from '../../../../model/sort-order';

@Component({
  selector: 'app-assets-step',
  templateUrl: './assets-step.component.html',
  styleUrls: ['./assets-step.component.scss']
})
export class AssetsStepComponent implements OnInit {

  @Input() tenant: Tenant;
  @Input() organization: Organization;
  @Input() selectionMode: 'single' | 'multiple';
  @Input() assetSelection: EntityWithUuid | EntityWithUuid[];
  @Input() assetFilters: AssetFilters;
  @Output() assetSelectionChange: EventEmitter<EntityWithUuid | EntityWithUuid[]>;
  @Output() assetFiltersChange: EventEmitter<AssetFilters>;

  public assets: Asset[];
  public totalAssets: number;
  public loadingAssets: boolean;
  public first: number;
  public rows: number;
  public representations: SelectItem<string>[];
  public organizations: Organization[];
  public selectAllCheckbox: boolean;

  constructor(
    private assetsService: AssetsService,
    private tenantsService: TenantsService,
    private confirmationService: ConfirmationService,
    private loggedUser: LoggedUser
  ) {
    this.assetSelectionChange = new EventEmitter<EntityWithUuid | EntityWithUuid[]>();
    this.assetFiltersChange = new EventEmitter<AssetFilters>();
    this.first = 0;
    this.rows = 10;
    this.assets = new Array(this.rows);
    this.loadingAssets = false;
  }

  ngOnInit(): void {
    this.loadAssets(0, this.rows);
    this.loadAssetRepresentations();
    this.loadOrganizations();
  }

  private loadAssets(offset: number, limit: number): void {
    this.loadingAssets = true;
    this.assetFilters.tenantUuids = [this.tenant.uuid];
    this.assetFilters.organizationUuids = this.organization ? [this.organization.uuid] : this.assetFilters.organizationUuids;
    this.assetsService.getAssets(offset, limit, this.assetFilters).subscribe(response => {
      this.assets = response.assets;
      this.totalAssets = response.totalElements;
      this.loadingAssets = false;
    });
  }

  private loadAssetRepresentations(): void {
    this.tenantsService.getTenantAssetRepresentations(this.tenant.uuid).subscribe(response => {
      this.representations = response.assetRepresentations
        .map((representation: AssetRepresentation) => representation.name)
        .sort()
        .map((value: string) => ({value}));
    });
  }

  private loadOrganizations(): void {
    this.tenantsService.getTenantOrganizationSummaries(this.tenant.uuid).subscribe(value => {
      this.organizations = value.organizations;
    });
  }

  public applyFilters(): void {
    this.first = 0;
    this.loadAssets(this.first / this.rows, this.rows);
  }

  public onAssetPageChange(event: any): void {
    this.assetFilters.sortField = event.sortField;
    this.assetFilters.sortOrder = event.sortOrder <= 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadAssets(event.first / event.rows, event.rows);
  }

  public displaySelectAllDialog(): void {
    this.confirmationService.confirm({
      message: translate('info.confirmSelectAll', { totalElements: this.totalAssets }),
      header: translate('common.selectAll'),
      acceptLabel: translate('common.accept'),
      rejectLabel: translate('common.cancel'),
      acceptIcon: 'pi pi-check',
      defaultFocus: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.selectAll()
    });
  }

  public selectAll(): void {
    this.assetFilters.tenantUuids = [this.tenant.uuid];
    this.assetFilters.organizationUuids = this.organization ? [this.organization.uuid] : this.assetFilters.organizationUuids;
    this.assetsService.getAssets(null, null, this.assetFilters).subscribe(response => {
      this.assetSelection = [...this.assetSelection as EntityWithUuid[]];
      this.assetSelection.push(...response.assets);
      this.assetSelectionChange.emit(this.assetSelection);
    });
  }

}
